/**
 * Página de Notificações — Central completa do usuário
 * Lista todas as notificações com filtros por categoria e paginação
 */
import { useState, useEffect, useCallback } from 'preact/hooks';
import { route } from 'preact-router';
import { api } from '../api/client';
import { ListSkeleton, Spinner, Button } from '../components/ui';
import { T } from '../styles/tokens';
import i18next from 'i18next';

/* ═══ Tema (dark, consistente com o sistema) ═══ */
const theme = {
  bg: T.bg,
  card: T.bgSecondary,
  cardHover: T.bg,
  border: T.border,
  text: T.text,
  muted: T.textMuted,
  accent: T.primary,
  unreadBg: T.primaryBg,
  unreadDot: T.primary,
};

/* ═══ Ícones por categoria ═══ */
const categoryIcons: Record<string, any> = {
  general: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/>
    </svg>
  ),
  billing: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
    </svg>
  ),
  security: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  promo: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/>
    </svg>
  ),
  system: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
    </svg>
  ),
};

/* ═══ Tempo relativo ═══ */
function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

/* ═══ Formatação de data completa ═══ */
function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

interface Notification {
  id: string;
  type: string;
  category: string;
  title: string;
  message: string;
  read: number;
  action_url: string | null;
  icon: string | null;
  created_at: string;
}

const CATEGORIES = [
  { key: 'all', label: i18next.t('common:profile.all_fem') },
  { key: 'general', label: i18next.t('common:profile.general') },
  { key: 'billing', label: i18next.t('common:notif_billing') },
  { key: 'security', label: i18next.t('common:admin.security') },
  { key: 'promo', label: i18next.t('admin:promotions') },
  { key: 'system', label: i18next.t('common:admin.system') },
];

export function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  const fetchNotifications = useCallback(async (pageNum: number, cat: string, append = false) => {
    try {
      if (pageNum === 1) setLoading(true); else setLoadingMore(true);
      const params = new URLSearchParams({ page: String(pageNum), limit: '20' });
      if (cat !== 'all') params.set('category', cat);
      const res = await api.get(`/notification-inbox?${params}`) as any;
      const items = res?.data?.notifications || res?.notifications || [];
      if (append) {
        setNotifications(prev => [...prev, ...items]);
      } else {
        setNotifications(items);
      }
      setHasMore(items.length === 20);
    } catch (e) {
      console.error('Erro ao carregar notificações:', e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    fetchNotifications(1, category);
  }, [category]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchNotifications(next, category, true);
  };

  const markAsRead = async (id: string) => {
    try {
      await api.patch(`/notification-inbox/${id}/read`, {});
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: 1 } : n));
    } catch {}
  };

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      await api.patch('/notification-inbox/read-all', {});
      setNotifications(prev => prev.map(n => ({ ...n, read: 1 })));
    } catch {} finally {
      setMarkingAll(false);
    }
  };

  const handleClick = (n: Notification) => {
    if (!n.read) markAsRead(n.id);
    if (n.action_url) {
      if (n.action_url.startsWith('http')) {
        window.open(n.action_url, '_blank');
      } else {
        route(n.action_url);
      }
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 24px' }}>
      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: theme.text, margin: 0 }}>Notificações</h1>
        {unreadCount > 0 && (
          <Button variant="ghost"
            onClick={markAllRead}
            disabled={markingAll}
            style={{
              background: 'transparent', border: `1px solid ${theme.border}`, borderRadius: 8,
              padding: '8px 16px', color: theme.accent, fontSize: 13, fontWeight: 500,
              cursor: markingAll ? 'not-allowed' : 'pointer', opacity: markingAll ? 0.5 : 1,
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e: any) => e.currentTarget.style.background = T.primaryBg}
            onMouseLeave={(e: any) => e.currentTarget.style.background = 'transparent'}
          >
            {markingAll ? i18next.t('common:processing') : i18next.t('common:notifications.mark_all_read')}
          </Button>
        )}
      </div>

      {/* Filtros por categoria */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {CATEGORIES.map(c => (
          <Button variant="ghost"
            key={c.key}
            onClick={() => setCategory(c.key)}
            style={{
              padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500,
              border: 'none', cursor: 'pointer', transition: 'all 0.15s',
              background: category === c.key ? theme.accent : T.border,
              color: category === c.key ? '#fff' : theme.muted,
            }}
          >
            {c.label}
          </Button>
        ))}
      </div>

      {/* Lista de notificações */}
      {loading ? (
        <div style={{ padding: 48 }}>
          <ListSkeleton rows={5} />
        </div>
      ) : notifications.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 64, color: theme.muted }}>
          <div style={{ marginBottom: 16, opacity: 0.3 }}><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#6B7A90" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg></div>
          <div style={{ fontSize: 16 }}>Nenhuma notificação encontrada</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {notifications.map(n => (
            <div
              key={n.id}
              onClick={() => handleClick(n)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                padding: '14px 16px', borderRadius: 10,
                background: !n.read ? theme.unreadBg : 'transparent',
                cursor: n.action_url ? 'pointer' : 'default',
                transition: 'background 0.15s',
                borderBottom: `1px solid ${theme.border}`,
              }}
              onMouseEnter={(e: any) => e.currentTarget.style.background = theme.cardHover}
              onMouseLeave={(e: any) => e.currentTarget.style.background = !n.read ? theme.unreadBg : 'transparent'}
            >
              {/* Indicador não lida */}
              <div style={{ width: 8, paddingTop: 6, flexShrink: 0 }}>
                {!n.read && (
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: theme.unreadDot }} />
                )}
              </div>

              {/* Ícone da categoria */}
              <div style={{ flexShrink: 0, color: theme.muted, paddingTop: 2 }}>
                {categoryIcons[n.category] || categoryIcons.general}
              </div>

              {/* Conteúdo */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: !n.read ? 600 : 500, color: theme.text, lineHeight: '20px' }}>
                    {n.title}
                  </div>
                  <div style={{ fontSize: 12, color: theme.muted, flexShrink: 0, whiteSpace: 'nowrap' }}>
                    {formatDate(n.created_at)}
                  </div>
                </div>
                <div style={{ fontSize: 13, color: theme.muted, lineHeight: '18px', marginTop: 4 }}>
                  {n.message}
                </div>
              </div>
            </div>
          ))}

          {/* Botão carregar mais */}
          {hasMore && (
            <div style={{ textAlign: 'center', padding: 16 }}>
              <Button variant="ghost"
                onClick={loadMore}
                disabled={loadingMore}
                style={{
                  background: T.border, border: 'none', borderRadius: 8,
                  padding: '10px 24px', color: theme.text, fontSize: 13, fontWeight: 500,
                  cursor: loadingMore ? 'not-allowed' : 'pointer', opacity: loadingMore ? 0.5 : 1,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgActive}
                onMouseLeave={(e: any) => e.currentTarget.style.background = T.border}
              >
                {loadingMore ? <><Spinner size="sm" inline /> {i18next.t('common:loading')}</> : i18next.t('common:profile.load_more')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
